import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Clock, Minus, Users } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router';

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableWrapper,
} from '@/components/deprecated/OldTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MEDIA_URL } from '@/config';
import { formatMapName } from '@/utils/format';
import { convertUnixTimestamp } from '@/utils/unix';

import { fetchLeagueMatches } from '../api/getLeagueMatches';

interface TeamScheduleProps {
  teamId: string;
}
const TeamSchedule: React.FC<TeamScheduleProps> = ({ teamId }) => {
  const navigate = useNavigate();
  const { data: matches, isLoading } = useQuery({
    queryKey: ['schedule', teamId],
    queryFn: () => fetchLeagueMatches(teamId),
  });

  const formatDateAndTime = (timestamp: number) => {
    const formatted = convertUnixTimestamp(timestamp);
    return (
      <>
        {formatted.timeWithZone}
        <br />
        {formatted.formattedDayAndMonth}
      </>
    );
  };

  const handleRowClick = (matchId: number) => {
    navigate(`/match/${matchId}`);
  };

  if (!matches) return null;

  return (
    <div className="inline-grid w-full flex-col overflow-hidden lg:flex">
      <div className="overflow-x-auto">
        <div className="mb-3 text-lg font-semibold">Schedule</div>
        <TableWrapper className="rounded-lg">
          <Table>
            <TableBody>
              {isLoading ? (
                <>
                  {Array.from({ length: 6 }, (_, index) => (
                    <TableRow key={index}>
                      <TableCell className="w-40">
                        <Skeleton className="h-2.5 w-40" />
                      </TableCell>
                      <TableCell className="flex items-center justify-end gap-x-2 pr-2!">
                        <Skeleton className="h-6 w-6 rounded-full!" />
                        <Skeleton className="h-2.5 w-40" />
                      </TableCell>
                      <TableCell className="px-0!">
                        <Skeleton className="h-2.5 w-10" />
                      </TableCell>
                      <TableCell className="flex flex-row-reverse items-center justify-end gap-x-2 pl-2!">
                        <Skeleton className="h-6 w-6 rounded-full!" />
                        <Skeleton className="h-2.5 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-12" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-2.5 w-2.5" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                <>
                  {matches.map((match) => (
                    <TableRow
                      key={match.id}
                      className="cursor-pointer transition-colors"
                      title="Go to match"
                      onClick={() => handleRowClick(match.id)}
                    >
                      <TableCell className="flex w-40 items-center gap-x-4">
                        <div className="w-4">
                          {match.leagueScheduleOffers &&
                          match.leagueScheduleOffers.length > 0 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Clock className="text-red" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  You have an unresolved rescheduling offer on
                                  this match.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            ''
                          )}
                        </div>
                        {formatDateAndTime(match.timestamp)}
                      </TableCell>
                      <TableCell className="flex items-center justify-end gap-x-2 pr-2">
                        <div className="text-right">
                          {match.team1.team_name}
                        </div>
                        <Avatar>
                          <AvatarImage
                            src={`${MEDIA_URL}/images/uploads/${match.team1.team_photo}`}
                            alt={`${match.team1.team_name}`}
                          />
                          <AvatarFallback className="bg-secondary">
                            <Users />
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="px-0 text-center text-lg!">
                        {match.winner_team_id == 0 ? (
                          <Minus className="mx-auto" />
                        ) : (
                          <>
                            <div
                              className={`mr-1 inline-block w-7 text-center ${
                                match.winner_team_id.toString() ==
                                match.team1.id
                                  ? 'text-[#0BEA21]'
                                  : 'text-red'
                              }`}
                            >
                              <>
                                {!match.get5StatsMatch.get5StatsMaps[0] ? (
                                  <span className="text-sm">
                                    {match.get5StatsMatch.winner == 'team2'
                                      ? 'FFL'
                                      : 'FFW'}
                                  </span>
                                ) : (
                                  <>
                                    {match.playoffs
                                      ? match.get5StatsMatch.team1_score
                                      : match.get5StatsMatch.get5StatsMaps[0]
                                          .team1_score}
                                  </>
                                )}
                              </>
                            </div>
                            :
                            <div
                              className={`ml-1 inline-block w-7 text-center ${
                                match.winner_team_id.toString() ==
                                match.team2.id
                                  ? 'text-[#0BEA21]'
                                  : 'text-red'
                              }`}
                            >
                              <>
                                {!match.get5StatsMatch.get5StatsMaps[0] ? (
                                  <span className="text-sm">
                                    {match.get5StatsMatch.winner == 'team1'
                                      ? 'FFL'
                                      : 'FFW'}
                                  </span>
                                ) : (
                                  <>
                                    {match.playoffs
                                      ? match.get5StatsMatch.team2_score
                                      : match.get5StatsMatch.get5StatsMaps[0]
                                          .team2_score}
                                  </>
                                )}
                              </>
                            </div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="flex flex-row-reverse items-center justify-end gap-x-2 pl-2">
                        <div>{match.team2.team_name}</div>
                        <Avatar>
                          <AvatarImage
                            src={`${MEDIA_URL}/images/uploads/${match.team2.team_photo}`}
                            alt={`${match.team2.team_name}`}
                          />
                          <AvatarFallback className="bg-secondary">
                            <Users />
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        {match.playoffs ? (
                          <>
                            {match.map1_name && match.map1_name !== 'bo3' ? (
                              <div className="hs-tooltip inline-block [--trigger:hover] sm:[--placement:right]">
                                <div className="hs-tooltip-toggle flex max-w-xs items-center gap-x-3 rounded-xl border border-border bg-muted px-2.5 py-1.5 shadow-xs">
                                  BO3
                                  <div
                                    className="hs-tooltip-content hs-tooltip-shown:visible hs-tooltip-shown:opacity-100 invisible absolute z-10 hidden rounded-xl border border-border bg-muted text-start opacity-0 shadow-md transition-opacity after:absolute after:-start-4 after:top-0 after:h-full after:w-4"
                                    role="tooltip"
                                  >
                                    <ul className="space-y-1 px-4 py-3">
                                      <li>
                                        <div className="inline-flex w-full items-center justify-center text-sm">
                                          {match.get5StatsMatch
                                            .get5StatsMaps[0] &&
                                            match.get5StatsMatch
                                              .get5StatsMaps[0].winner && (
                                              <div
                                                className={`mr-1 inline-block w-7 text-center ${
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[0].winner ==
                                                  'team1'
                                                    ? 'text-[#0BEA21]'
                                                    : 'text-red'
                                                }`}
                                              >
                                                {
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[0]
                                                    .team1_score
                                                }
                                              </div>
                                            )}
                                          <div className="w-12">
                                            <img
                                              src={`/images/${match.map1_name}.jpg`}
                                              className="rounded-lg border border-border"
                                              title={formatMapName(
                                                match.map1_name,
                                              )}
                                              alt=""
                                            />
                                          </div>
                                          {match.get5StatsMatch
                                            .get5StatsMaps[0] &&
                                            match.get5StatsMatch
                                              .get5StatsMaps[0].winner && (
                                              <div
                                                className={`mr-1 inline-block w-7 text-center ${
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[0].winner ==
                                                  'team2'
                                                    ? 'text-[#0BEA21]'
                                                    : 'text-red'
                                                }`}
                                              >
                                                {
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[0]
                                                    .team2_score
                                                }
                                              </div>
                                            )}
                                        </div>
                                      </li>

                                      <li>
                                        <div className="inline-flex w-full items-center justify-center text-sm">
                                          {match.get5StatsMatch
                                            .get5StatsMaps[1] &&
                                            match.get5StatsMatch
                                              .get5StatsMaps[1].winner && (
                                              <div
                                                className={`mr-1 inline-block w-7 text-center ${
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[1].winner ==
                                                  'team1'
                                                    ? 'text-[#0BEA21]'
                                                    : 'text-red'
                                                }`}
                                              >
                                                {
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[1]
                                                    .team1_score
                                                }
                                              </div>
                                            )}
                                          <div className="w-12">
                                            {match.map2_name ? (
                                              <img
                                                src={`/images/${match.map2_name}.jpg`}
                                                className="rounded-lg border border-border"
                                                title={formatMapName(
                                                  match.map2_name,
                                                )}
                                                alt=""
                                              />
                                            ) : (
                                              <div className="mb-1 h-auto rounded-lg border border-border py-1.5 text-center">
                                                TBD
                                              </div>
                                            )}
                                          </div>
                                          {match.get5StatsMatch
                                            .get5StatsMaps[1] &&
                                            match.get5StatsMatch
                                              .get5StatsMaps[1].winner && (
                                              <div
                                                className={`mr-1 inline-block w-7 text-center ${
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[1].winner ==
                                                  'team2'
                                                    ? 'text-[#0BEA21]'
                                                    : 'text-red'
                                                }`}
                                              >
                                                {
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[1]
                                                    .team2_score
                                                }
                                              </div>
                                            )}
                                        </div>
                                      </li>
                                      <li>
                                        <div className="inline-flex w-full items-center justify-center text-sm">
                                          {match.get5StatsMatch
                                            .get5StatsMaps[2] &&
                                            match.get5StatsMatch
                                              .get5StatsMaps[2].winner && (
                                              <div
                                                className={`mr-1 inline-block w-7 text-center ${
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[2].winner ==
                                                  'team1'
                                                    ? 'text-[#0BEA21]'
                                                    : 'text-red'
                                                }`}
                                              >
                                                {
                                                  match.get5StatsMatch
                                                    .get5StatsMaps[2]
                                                    .team1_score
                                                }
                                              </div>
                                            )}
                                          <div className="w-12">
                                            {match.map3_name ? (
                                              <img
                                                src={`/images/${match.map3_name}.jpg`}
                                                className="rounded-lg border border-border"
                                                title={formatMapName(
                                                  match.map3_name,
                                                )}
                                                alt=""
                                              />
                                            ) : (
                                              <div className="mb-1 h-auto rounded-lg border border-border py-1.5 text-center">
                                                TBD
                                              </div>
                                            )}
                                          </div>
                                          {match.get5StatsMatch
                                            .get5StatsMaps[2] && (
                                            <div
                                              className={`mr-1 inline-block w-7 text-center ${
                                                match.get5StatsMatch
                                                  .get5StatsMaps[2].winner ==
                                                'team2'
                                                  ? 'text-[#0BEA21]'
                                                  : 'text-red'
                                              }`}
                                            >
                                              {
                                                match.get5StatsMatch
                                                  .get5StatsMaps[2].team2_score
                                              }
                                            </div>
                                          )}
                                        </div>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div>Pending</div>
                            )}
                          </>
                        ) : (
                          <>
                            {match.map1_name ? (
                              <div className="w-12">
                                <img
                                  src={`/images/${match.map1_name}.jpg`}
                                  className="rounded-lg border border-border"
                                  title={formatMapName(match.map1_name)}
                                  alt=""
                                />
                              </div>
                            ) : (
                              'Pending'
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <ChevronRight />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </div>
    </div>
  );
};

export default TeamSchedule;
